import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/Navbar.css';
import ContactButton from './ContactButton';

interface ServiceItem {
  title: string;
  path: string;
  imageSrc: string;
  highlighted?: boolean;
}

const serviceTypes: ServiceItem[] = [
  { title: "Cloud Cost Optimization", path: "/services/cloud-cost-optimization", imageSrc: "/assets/cloud.svg" },
  { title: "Kubernetes Consulting", path: "/services/kubernetes-consulting", imageSrc: "/assets/services/kubernetes.png" },
  { title: "AI & GenAI Integration", path: "/services/ai-genai-integration", imageSrc: "/assets/ai.svg" },
  { title: "MLOps & ML Platform", path: "/services/mlops-platform-engineering", imageSrc: "/assets/ml.svg" },
  { title: "Terraform & Pulumi", path: "/services/terraform-pulumi", imageSrc: "/assets/services/terraform.png" },
  { title: "SRE Consulting", path: "/services/sre-consulting", imageSrc: "/static/media/public-sector.ef32cfe6a148d776cde0.png" },
  { title: "Prometheus & Grafana", path: "/services/prometheus-grafana", imageSrc: "/assets/services/PrometheusGrafana.png" },
  { title: "Penetration Testing", path: "/services/penetration-testing", imageSrc: "/assets/services/vulnerability-left-image.png" },
  { title: "Red Team & Adversary Simulation", path: "/services/red-team-adversary-simulation", imageSrc: "/assets/services/Red-Team-Assessment.png" },
  { title: "Configuration Security Review", path: "/services/configuration-security-review", imageSrc: "/assets/services/kubernetes-left-image.png" },
  { title: "Compliance & Framework Assessments", path: "/services/compliance-framework-assessment", imageSrc: "/static/media/img5.48d8cc8c33ba78943ace.png" },
];

const industries = [
  { title: "FinTech & BFSI", path: "/industries/finance-banking", imageSrc: "/assets/finance.png" },
  { title: "Government & Public Sector", path: "/industries/government-public-sector", imageSrc: "/assets/services/governmentSector.png" },
  { title: "Healthcare", path: "/industries/healthcare-life-sciences", imageSrc: "/assets/industry/healthCare-left-image.png" },
  { title: "E-commerce & Retail", path: "/industries/retail-ecommerce", imageSrc: "/assets/industry/retail-right-image.png" },
  { title: "Manufacturing Industry 4.0", path: "/industries/manufacturing-industry-4", imageSrc: "/static/media/manufacturing.94b9f3ee6c6941ee4332.png" },
];


const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeServiceImage, setActiveServiceImage] = useState(serviceTypes[0].imageSrc);

  const location = useLocation();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const navbarRef = useRef<HTMLElement>(null);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Check if the given path is the current active route
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleServiceHover = (imageSrc: string) => {
    setActiveServiceImage(imageSrc);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMenuOpen &&
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target as Node) &&
        !menuButtonRef.current?.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    // Add event listener when menu is open
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <nav className="navbar-container" ref={navbarRef}>
      <div className="logo">
        <Link to="/" className="logo-text">reventlabs</Link>
      </div>

      {/* Desktop Navigation */}
      <div className="desktop-nav">
        <div className="nav-item-with-dropdown">
          <Link
            to="/services"
            className={`nav-link ${isActive('/services') ? 'active-nav-link' : ''}`}
          >
            SERVICES
            <img src="/assets/arrow-down.svg" alt="Dropdown Arrow" className="dropdown-arrow" />
          </Link>

          {/* Services Dropdown */}
          <div className="services-dropdown">
            <div className="services-dropdown-content">
              <div className="services-dropdown-image">
                <img src={activeServiceImage} alt="Service" />
              </div>
              <div className="services-dropdown-lists">
                <div className="services-dropdown-column">
                  <h3>Services</h3>
                  <ul>
                    {serviceTypes.map((service, index) => (
                      <li key={`service-${index}`}>
                        <Link
                          to={service.path}
                          onMouseEnter={() => handleServiceHover(service.imageSrc)}
                        >
                          {service.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="services-dropdown-column">
                  <h3>Industries</h3>
                  <ul>
                    {industries.map((industry, index) => (
                      <li key={`industry-${index}`}>
                        <Link
                          to={industry.path}
                          onMouseEnter={() => handleServiceHover(industry.imageSrc)}
                        >
                          {industry.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Link to="/industries" className={`nav-link ${isActive('/industries') ? 'active-nav-link' : ''}`}>INDUSTRIES</Link>
        <Link to="/why-us" className={`nav-link ${isActive('/why-us') ? 'active-nav-link' : ''}`}>WHY US ?</Link>
        <Link to="/security" className={`nav-link ${isActive('/security') ? 'active-nav-link' : ''}`}>SECURITY</Link>
        <Link to="/about-us" className={`nav-link ${isActive('/about-us') ? 'active-nav-link' : ''}`}>ABOUT US</Link>
      </div>

      {/* Desktop Contact Button */}
      <div className="desktop-contact">
        <ContactButton variant="border">CONTACT US</ContactButton>
      </div>

      {/* Mobile Menu Button */}
      <div className="mobile-menu-button">
        <button
          ref={menuButtonRef}
          onClick={toggleMenu}
          className="menu-toggle"
          aria-label="Toggle menu"
        >
          {isMenuOpen ? (
            <svg className="menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMenuOpen ? 'mobile-menu-open' : 'mobile-menu-closed'}`}
        ref={mobileMenuRef}
      >
        <div className="mobile-menu-content">
          <Link to="/why-us" className={`mobile-nav-link ${isActive('/why-us') ? 'active-nav-link' : ''}`} onClick={toggleMenu}>WHY US ?</Link>
          <Link to="/services" className={`mobile-nav-link ${isActive('/services') ? 'active-nav-link' : ''}`} onClick={toggleMenu}>SERVICES</Link>
          <Link to="/industries" className={`mobile-nav-link ${isActive('/industries') ? 'active-nav-link' : ''}`} onClick={toggleMenu}>INDUSTRIES</Link>
          <Link to="/security" className={`mobile-nav-link ${isActive('/security') ? 'active-nav-link' : ''}`} onClick={toggleMenu}>SECURITY</Link>
          <Link to="/about-us" className={`mobile-nav-link ${isActive('/about-us') ? 'active-nav-link' : ''}`} onClick={toggleMenu}>ABOUT US</Link>
          <ContactButton className="mobile-contact-button" onClick={toggleMenu} variant="border">CONTACT US</ContactButton>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;